/**
 * Contratos HTTP consumidos pela Plataforma (Task 10 / R2P).
 *
 * Regra de ownership: a Plataforma NÃO define regra de negócio nem inventa
 * formato. Os tipos abaixo são o MESMO contrato já servido por
 * `apps/api-dev/src/server.ts` (dev/E2E) e pelas Edge Functions
 * `supabase/functions/*` (produção). Onde o contrato já existe em
 * `packages/*`, importamos o tipo de lá em vez de redeclarar — é isso que
 * garante que a tela e o backend falem do MESMO registro.
 *
 * As funções `assert*` deste arquivo são validação de BORDA (o JSON chegou do
 * servidor; nada garante o formato até validarmos). Mesmo estilo das
 * `assert*` de `packages/ui/src/types.ts`, que também são reusadas pelo
 * cliente (`platform-client.ts`) para as entidades que já têm contrato lá.
 */
import type { ChatConversationExtras, ChatMessage } from "./ai-types.js";
import type { OrderCartItem, OrderCustomer, OrderPayment } from "../../../../packages/checkout/src/index.js";
// Entrega 0 (R3–R5): o contrato da configuração de integrações é o MESMO que
// packages/config serializa (metadados apenas, nunca o valor de um segredo).
// Reusar o tipo do pacote impede a tela de declarar um formato paralelo.
import type { SerializedIntegration } from "../../../../packages/config/src/index.js";
import type { CatalogListEntry } from "../../../../packages/catalog/src/index.js";
import type { FlowDefinition } from "../../../../packages/flows/src/index.js";
import type { Conversation } from "../../../../packages/conversation/src/index.js";
// Task 12: o vocabulário de estado/origem do pedido unificado vem da máquina de
// estados pura (espelho dos `check` da migration 0031), não de uma segunda
// lista declarada aqui — um pedido não pode ter dois conjuntos de estados.
import { isOrderOrigin, isOrderState, type PlatformOrderOrigin, type PlatformOrderState } from "../orders/state-machine.js";
// Task 14: o vocabulário de etapa/ocorrência da expedição vem da máquina de
// etapas pura (espelho dos `check` da migration 0033), não de uma lista
// declarada aqui — uma expedição não pode ter dois conjuntos de etapas.
import { isFulfillmentStage, isOccurrenceKind, type FulfillmentStage, type OccurrenceKind } from "../fulfillment/state-machine.js";
// Task 15: o vocabulário de estado/resolução do RMA vem da máquina de estados
// pura (espelho dos `check` da migration 0034), não de uma lista declarada
// aqui — um caso de garantia não pode ter dois conjuntos de etapas.
import { isRmaResolution, isRmaState, type RmaResolution, type RmaState } from "../rma/state-machine.js";
// Task 16: o vocabulário de status de conciliação e tipo de conta gerencial vem
// da regra pura do financeiro (espelho dos `check` da migration 0035), não de
// uma lista declarada aqui — a conciliação não pode ter dois conjuntos de
// status, nem a conta gerencial dois conjuntos de tipos.
import {
  isManagerialAccountKind,
  isReconciliationState,
  type ManagerialAccountKind,
  type ReconciliationState
} from "../financial/managerial.js";
// Task 13 (limpeza de arquitetura): o vocabulário e os contratos do CRM moram
// agora em `packages/crm` (regra pura promovida para o núcleo). Os tipos de
// entidade e as validações de borda são REEXPORTADOS mais abaixo; o estágio do
// funil, a situação da proposta e o tipo de tarefa são o MESMO `LeadStage` que
// `packages/ui` já definia, consumidos via `packages/crm`.

/** Pedido como o checkout-runtime (e o `apps/api-dev`) devolve. */
export interface PlatformOrder {
  id: string;
  placedAt: string;
  status: "awaiting_payment" | "awaiting_shipping_quote" | "paid" | "payment_failed";
  items: OrderCartItem[];
  customer: OrderCustomer;
  subtotalCents: number;
  shippingCents: number | null;
  totalCents: number | null;
  shippingStatus: "free" | "requires_manual_quote";
  shippingNote: string;
  trackingCode: string | null;
  carrier: string | null;
  payments: OrderPayment[];
}

/**
 * Conversa real (web chat + WhatsApp/WAPI da TDZAIN). A inbox rica acrescenta, de forma OPCIONAL
 * (conversa web e simulador antigo não trazem): tipo, contato com foto, grupo, participantes, não
 * lidas e prévia; e cada mensagem pode trazer autor, mídia, reações, citação e status de entrega.
 */
export type PlatformConversation = Omit<Conversation, "messages"> & ChatConversationExtras & {
  messages: Array<Conversation["messages"][number] & Partial<Omit<ChatMessage, "id" | "text" | "sender" | "createdAt">>>;
};

/** Grupo de WhatsApp da instância (Fase 4). `simulated` = veio do simulador, não do WhatsApp real. */
export interface WhatsAppGroup { id: string; name: string; size: number | null; simulated: boolean }

export function assertWhatsAppGroup(value: unknown): WhatsAppGroup {
  if (!isRecord(value)) fail("WhatsAppGroup", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("WhatsAppGroup", "missing id");
  if (typeof value.name !== "string") fail("WhatsAppGroup", "missing name");
  return { id: value.id, name: value.name, size: typeof value.size === "number" ? value.size : null, simulated: value.simulated === true };
}

/** Fluxo de atendimento salvo (Fase 5): definição de `packages/flows` + id e nome. */
export type SavedFlow = FlowDefinition & { id: string; name: string };
export type SavedFlowInput = FlowDefinition & { name: string };

export function assertSavedFlow(value: unknown): SavedFlow {
  if (!isRecord(value)) fail("SavedFlow", "payload is not an object");
  for (const key of ["id", "name", "triggerKeyword", "startStepId"] as const) {
    if (typeof value[key] !== "string" || !value[key]) fail("SavedFlow", `missing ${key}`);
  }
  if (!Array.isArray(value.steps)) fail("SavedFlow", "steps must be an array");
  return value as unknown as SavedFlow;
}

/** Resposta rápida do atendente (atalho → texto). Nunca é enviada sozinha. */
export interface QuickReply { id: string; shortcut: string; title: string; body: string }
export type QuickReplyInput = Pick<QuickReply, "shortcut" | "title" | "body">;

export function assertQuickReply(value: unknown): QuickReply {
  if (!isRecord(value)) fail("QuickReply", "payload is not an object");
  for (const key of ["id", "shortcut", "title", "body"] as const) {
    if (typeof value[key] !== "string" || !value[key]) fail("QuickReply", `missing ${key}`);
  }
  return value as unknown as QuickReply;
}

/** Entrada de catálogo (produto + oferta vigente) de `packages/catalog`. */
export type PlatformCatalogEntry = CatalogListEntry;

// ---------------------------------------------------------------------------
// Task 12 (brief 3.3) — pedido UNIFICADO multicanal (`platform_orders` +
// `platform_order_events`, migration 0031).
//
// `PlatformOrder` (acima) continua sendo o pedido do CHECKOUT da Loja: o
// registro de origem, servido por `/api/checkout/orders`. `PlatformUnifiedOrder`
// é o pedido da gestão centralizada, que **referencia** aquele registro
// (`checkoutOrderId`) em vez de recadastrá-lo (D-12.1) — é assim que "mesmos
// registros, mesmo histórico" vale para pedido da Loja, venda do WhatsApp e
// outros canais na mesma lista.
// ---------------------------------------------------------------------------

/**
 * Transição de estado registrada (`platform_order_events`). É o histórico
 * auditável do pedido: quem, quando, de onde para onde e com que justificativa.
 *
 * `actorKind: "system"` marca a transição que a Plataforma espelhou de um
 * registro de origem (ex.: o pagamento aprovado no checkout da Loja) em vez de
 * um operador tê-la registrado — a diferença fica visível na linha do tempo,
 * nunca apagada.
 */
export interface PlatformOrderEvent {
  id: string;
  platformOrderId: string;
  /** `null` apenas no evento de criação (o pedido não vinha de estado algum). */
  fromState: PlatformOrderState | null;
  toState: PlatformOrderState;
  actor: string;
  actorKind: "staff" | "system";
  reason: string;
  /** Dado que justificou a transição (referência de pagamento/NF/rastreio). */
  payload: Record<string, unknown>;
  occurredAt: string;
}

/** Estado da carga incremental Bling -> Supabase (`bling-orders-sync`). */
export interface OrdersSyncState {
  lastSyncedAt: string | null;
  lastRunAt: string | null;
  status: "ok" | "partial" | "error" | null;
  count: number;
  error: string | null;
}

export interface OrdersSyncResult {
  status: "ok" | "partial" | "error" | "skipped";
  processed?: number;
  reason?: string;
  error?: string | null;
}

export interface PlatformUnifiedOrder {
  id: string;
  /** Canal de origem — preservado, nunca normalizado para "um canal só". */
  origin: PlatformOrderOrigin;
  /** Identificador do pedido NO canal de origem (brief 3.3). */
  externalId: string;
  /** Pedido do checkout da Loja que este registro projeta; `null` nos outros canais. */
  checkoutOrderId: string | null;
  /** Conversa de origem (D-12.4), quando a venda nasceu no atendimento. */
  conversationId: string | null;
  /** Cliente canônico (`customers`, 0020) resolvido pela identidade do canal. */
  customerId: string | null;
  customer: OrderCustomer | null;
  state: PlatformOrderState;
  /** Explicação do estado atual (inclui a tradução do status do checkout). */
  stateNote: string;
  items: OrderCartItem[];
  subtotalCents: number;
  shippingCents: number | null;
  totalCents: number | null;
  payments: OrderPayment[];
  /** Referência da NF emitida no Bling. A Plataforma não emite (D-12.3). */
  nfReference: string | null;
  carrier: string | null;
  trackingCode: string | null;
  cancellationReason: string | null;
  returnReason: string | null;
  placedAt: string;
  updatedAt: string;
  /** Canal de venda real do Bling (slug: mercado_livre, shopee, magalu, ...). Ausente em pedidos da Loja/WhatsApp. */
  channelKind?: string | null;
  /** Nome da conta do canal no Bling ("Mercado Livre", "Shopee 02"). */
  channelName?: string | null;
  /** Situação original do pedido no Bling ("Em aberto", "Atendido"), sem tradução. */
  blingSituation?: string | null;
  /** Quando este pedido foi relido do Bling pela última vez. */
  blingSyncedAt?: string | null;
  /** Origem de aquisição (0062): slug de `sales_origin_options`. Nulo = não informada. Não confundir com `origin`/`channelKind`. */
  acquisitionOrigin?: string | null;
  /** Estratégia/campanha em texto livre (ex.: "Black Friday 2026"). */
  acquisitionStrategy?: string | null;
  /** Como a origem foi definida: manual, anúncio Meta, link do site ou herdada da conversa. */
  acquisitionSource?: "manual" | "meta_ad" | "site_link" | "inherited" | null;
  /** Quem fechou a venda (texto; aceita membro da equipe ou nome livre). */
  responsibleName?: string | null;
  /** Resposta integral do Bling para o pedido (itens com SKU, taxas, frete, transporte...). Só vem no detalhe. */
  blingRaw?: Record<string, unknown> | null;
  events: PlatformOrderEvent[];
}

// ---------------------------------------------------------------------------
// Task 13 (brief 3.4) — CRM 360: pipeline, orçamento, tarefas e segmentos
// (`crm_opportunities`/`crm_opportunity_events`/`crm_quotes`/`crm_quote_items`/
// `crm_tasks`/`crm_segments`, migration 0032).
//
// Limpeza de arquitetura da Task 13: os contratos das entidades do CRM e suas
// validações de borda `assertCrm*` foram promovidos para o núcleo compartilhado
// `packages/crm` (ADR-0004 / handoffs H-12.A/H-13.A), para a Edge Function
// `supabase/functions/crm-runtime` e este app lerem o MESMO contrato sem a
// borda depender do app. Aqui apenas REEXPORTAMOS, para os importadores
// existentes (`../api/types.js`) continuarem funcionando sem mudança.
//
// O que NÃO existe, de propósito: um "cliente do CRM". O cadastro é o cliente
// CANÔNICO (`customers` + `customer_identities`, 0020) e a empresa é
// `companies`/`company_members` (0018); `CrmCustomer`/`CrmCompany` são a
// leitura DESSES registros, não um cadastro paralelo.
// ---------------------------------------------------------------------------

export {
  assertCrmCompany,
  assertCrmCustomer,
  assertCrmOpportunity,
  assertCrmQuote,
  assertCrmSegment,
  assertCrmTask
} from "../../../../packages/crm/src/entities.js";
export type {
  CrmCompany,
  CrmCompanyContact,
  CrmCustomer,
  CrmCustomerIdentity,
  CrmOpportunity,
  CrmOpportunityEvent,
  CrmQuote,
  CrmQuoteItem,
  CrmSegment,
  CrmTask
} from "../../../../packages/crm/src/entities.js";

export interface PricingMarginRow {
  productId: string;
  name: string;
  priceCents: number | null;
  costCents: number | null;
  marginPct: number | null;
  status: "ok" | "below_target" | "missing_cost" | "no_offer";
}

export interface PricingSummary {
  analyzed: number;
  belowTarget: number;
  missingCost: number;
  pendingRecommendations: number;
  /** `null` quando nenhum produto tem custo sincronizado — não inventamos margem. */
  avgMarginPct: number | null;
  minMarginPct: number;
}

export interface PricingSummaryResponse {
  summary: PricingSummary;
  rows: PricingMarginRow[];
}

/** Notificação operacional do admin-runtime (snake_case, como vem do banco). */
export interface PlatformNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  severity: "info" | "warning" | "critical";
  read: boolean;
  related_entity_type: string | null;
  related_entity_id: string | null;
  created_at: string;
}

export interface PlatformMetrics {
  totalEvents: number;
  paidOrders: number;
  conversations: number;
  aiUsage: {
    totalCalls: number;
    totalPromptTokens: number;
    totalCompletionTokens: number;
    avgLatencyMs: number;
    callsByModel: Record<string, number>;
  };
  generatedAt: string;
}

/**
 * Passo registrado no caso de garantia (`rma_case_events`, migration 0034). É o
 * histórico auditável: quem, quando, de que etapa para qual, por quê, com qual
 * responsável/prazo, quais evidências e qual comunicação ao cliente.
 *
 * `actorKind: "system"` marca o passo espelhado de um registro de origem (ex.:
 * comunicação disparada automaticamente), nunca apresentado como ação de
 * operador.
 */
export interface PlatformRmaEvent {
  id: string;
  rmaCaseId: string;
  /** `null` no evento de criação (o caso não vinha de etapa alguma). */
  fromState: RmaState | null;
  toState: RmaState;
  actor: string;
  actorKind: "staff" | "system";
  /** Responsável e prazo definidos PARA a etapa de destino. */
  assignedTo: string | null;
  dueAt: string | null;
  reason: string;
  /** Evidências do passo (URLs de storage; simuladas em dev). */
  evidenceUrls: string[];
  /** Comunicação da etapa: canal + resumo, nunca a mensagem crua/sensível. */
  communicationChannel: "whatsapp" | "email" | "phone" | "none" | null;
  communicationSummary: string;
  payload: Record<string, unknown>;
  occurredAt: string;
}

/**
 * Caso de garantia/RMA (`rma_cases`, migration 0034). O contrato nasceu na
 * Task 10 (só `id`/`state`/`openedAt`) para o KPI de garantias existir com
 * drill-down desde o primeiro dia; a Task 15 publica a FONTE e completa o
 * contrato com o ciclo inteiro. O cliente ainda devolve `null` quando a rota
 * não existe no ambiente — nunca 0 como se fosse "nenhuma garantia aberta".
 *
 * O caso REFERENCIA o pedido unificado e o cliente canônico (não recadastra):
 * é a ordem de assistência (etapa, prova, responsável, prazo, resolução), não
 * uma segunda cópia da venda. O vocabulário de estado/resolução vem da máquina
 * pura (espelho dos `check` da 0034).
 */
export interface PlatformRmaCase {
  id: string;
  customerId: string;
  customerName: string | null;
  platformOrderId: string | null;
  orderExternalId: string | null;
  productId: string | null;
  productName: string | null;
  state: RmaState;
  protocolCode: string | null;
  serialNumber: string | null;
  proofUrl: string | null;
  defectSummary: string;
  resolution: RmaResolution | null;
  /** Referência gerada para o Financeiro (Task 16); o RMA não paga (D-15.3). */
  financialReference: string | null;
  assignedTo: string | null;
  dueAt: string | null;
  openedAt: string;
  closedAt: string | null;
  updatedAt: string;
  /** Caso de EXEMPLO (seed demo): a tela exibe o selo "Exemplo". */
  demo?: boolean;
  events: PlatformRmaEvent[];
}

// ---------------------------------------------------------------------------
// Task 11 (brief 3.2) — catálogo em duas camadas, estoque/reservas e o lado
// operacional do Bling. Os dois contratos abaixo são as DUAS FONTES que a tela
// de divergências compara item a item; nenhum dos dois traz "divergência"
// pronta, porque a comparação é regra pura (`packages/catalog`, promovida na
// Fase 2 do Plano de Ataque R8 — ver ADR-0005). Os tipos abaixo são o
// contrato HTTP validado nesta fronteira; a regra pura os consome por
// subtipagem estrutural, sem reimportar este arquivo (packages/* não
// depende de apps/*).
// ---------------------------------------------------------------------------

export interface CatalogVariant {
  sku: string;
  label: string;
  stock: number;
}

export interface CatalogKitItem {
  sku: string;
  quantity: number;
}

export interface CatalogFiscalData {
  ncm: string;
  origin: string;
  cfop: string;
}

/** Camada operacional (autoridade Bling): SKU, estoque, preço-base, custo, fiscal. */
export interface PlatformCatalogOperational {
  sku: string;
  stock: number | null;
  basePriceCents: number | null;
  costCents: number | null;
  supplier: string | null;
  category: string | null;
  variants: CatalogVariant[];
  kitItems: CatalogKitItem[];
  fiscalData: CatalogFiscalData | null;
  /** `null` = produto local sem vínculo no Bling (divergência de vínculo). */
  blingId: string | null;
  lastSyncedAt: string | null;
  /** Evento de sync que originou o estado atual (drill-down da divergência). */
  syncLogId: string | null;
}

/** Camada comercial (autoridade Plataforma): nome, descrição, SEO, mídia. */
export interface PlatformCatalogCommercial {
  commercialName: string | null;
  shortDescription: string | null;
  longDescription: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  published: boolean;
  media: Array<{ id: string; kind: string; url: string; title: string; position: number }>;
}

export interface B2bPriceTable {
  id: string;
  name: string;
  companyId: string | null;
  active: boolean;
  entries: Array<{ minQuantity: number; packageMultiple: number; priceCents: number }>;
}

/** Reserva comercial sobre o estoque operacional (`stock_reservations`, 0019). */
export interface StockReservation {
  id: string;
  quantity: number;
  status: "active" | "released" | "consumed";
  reason: string;
  expiresAt: string | null;
  createdAt: string;
}

/** Histórico de alteração por campo (`catalog_change_history`, migration 0030). */
export interface CatalogChangeHistoryEntry {
  id: string;
  fieldKey: string;
  previousValue: unknown;
  newValue: unknown;
  source: "bling_sync" | "platform_edit" | "system";
  actor: string | null;
  changedAt: string;
}

/** Produto na visão de duas camadas da Plataforma (`GET /api/catalog/platform/products`). */
export interface PlatformCatalogProduct {
  productId: string;
  sku: string;
  name: string;
  channel: string;
  operational: PlatformCatalogOperational;
  commercial: PlatformCatalogCommercial;
  /** Oferta vendável vigente; `null` = nada a vender (disponibilidade 0). */
  sellableOffer: { id: string; channel: string; priceCents: number; stock: number } | null;
  b2bPriceTables: B2bPriceTable[];
  reservations: StockReservation[];
  changeHistory: CatalogChangeHistoryEntry[];
}

/**
 * Item do snapshot operacional do Bling.
 *
 * Campos OPCIONAIS de propósito: ausente significa "o adapter não publica este
 * campo" (nada a comparar) e `null`/valor significa "o Bling diz isto". A
 * comparação item a item só considera campo presente nos dois lados — é assim
 * que o simulador e o Bling real convivem sem inventar divergência.
 */
export interface BlingProductSnapshotItem {
  blingId: string;
  sku: string;
  /** Vínculo local; `null` = item do Bling sem produto na Plataforma. */
  productId: string | null;
  syncLogId: string | null;
  name?: string;
  priceCents?: number;
  stock?: number;
  costCents?: number;
  supplier?: string;
  category?: string;
  variants?: CatalogVariant[];
  kitItems?: CatalogKitItem[];
  fiscalData?: CatalogFiscalData;
}

/**
 * Evento de sincronização do Bling (`GET /api/catalog/bling/sync-log`), como a
 * tabela `bling_sync_log` (migration 0008 + 0015) o guarda — snake_case, igual
 * às notificações, porque é linha de banco.
 *
 * É o REGISTRO-FONTE da divergência: `CatalogDivergence.syncLogId` aponta para
 * um `id` deste conjunto, e a tela de sincronização seleciona e exibe esse
 * evento. Sem ele o link da divergência não teria destino real. `payload` não
 * é publicado (a rota expõe a identificação do evento, não o conteúdo
 * sincronizado) — mesma regra da trilha de auditoria.
 */
export interface BlingSyncLogEntry {
  id: string;
  entity_type: "product" | "order" | "invoice" | "shipment";
  local_id: string;
  bling_id: string | null;
  direction: "push" | "pull";
  status: "synced" | "conflict" | "failed";
  created_at: string;
}

/**
 * Resultado do reprocessamento de uma divergência (Task 17/R3,
 * `POST /api/catalog/bling/reprocess`): o item foi enfileirado em
 * `bling_sync_queue` (0037) para a fila de retry do sync do Bling.
 */
export interface BlingReprocessResult {
  status: "queued";
  queueId?: string;
  entityType: string;
  entityId: string;
}

/**
 * Snapshot operacional (`GET /api/catalog/bling/snapshot`).
 * `credential: "missing"` + `source: "simulator"` = integração **verde em
 * código, aguardando secret**: o caminho real é o mesmo, só falta a credencial.
 */
export interface BlingCatalogSnapshot {
  source: "bling" | "simulator";
  credential: "configured" | "missing";
  fetchedAt: string;
  items: BlingProductSnapshotItem[];
}

/**
 * Evento do event store como `GET /api/admin/audit` o expõe: identificadores
 * e ator, **sem payload** (o admin-runtime não publica o conteúdo do evento).
 * É o que alimenta a timeline de auditoria do Customer 360; o que não vem na
 * rota não é inventado na tela.
 */
export interface PlatformAuditEvent {
  id: string;
  occurredAt: string;
  eventType: string;
  aggregateType: string;
  aggregateId: string;
  actorType: "customer" | "human" | "ai" | "system";
  actorId: string | null;
  correlationId: string;
  causationId: string | null;
  idempotencyKey: string;
}

function fail(entity: string, reason: string): never {
  throw new Error(`Invalid ${entity}: ${reason}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function assertPlatformOrder(value: unknown): PlatformOrder {
  if (!isRecord(value)) fail("PlatformOrder", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformOrder", "missing id");
  if (typeof value.placedAt !== "string") fail("PlatformOrder", "missing placedAt");
  if (!Array.isArray(value.items)) fail("PlatformOrder", "items must be an array");
  if (typeof value.subtotalCents !== "number") fail("PlatformOrder", "missing subtotalCents");
  if (value.totalCents !== null && typeof value.totalCents !== "number") {
    fail("PlatformOrder", "totalCents must be a number or null");
  }
  const status = value.status;
  if (status !== "awaiting_payment" && status !== "awaiting_shipping_quote" && status !== "paid" && status !== "payment_failed") {
    fail("PlatformOrder", `unknown status ${String(status)}`);
  }
  return value as unknown as PlatformOrder;
}

/**
 * Validação de borda do pedido unificado (Task 12).
 *
 * Exige o que o brief exige: ORIGEM reconhecida, identificador externo
 * presente e estado dentro da máquina. Origem desconhecida ou `externalId`
 * vazio falham aqui em vez de virar uma linha de lista sem procedência —
 * preservar a origem é requisito, não enfeite.
 */
export function assertPlatformUnifiedOrder(value: unknown): PlatformUnifiedOrder {
  if (!isRecord(value)) fail("PlatformUnifiedOrder", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformUnifiedOrder", "missing id");
  if (!isOrderOrigin(value.origin)) fail("PlatformUnifiedOrder", `unknown origin ${String(value.origin)}`);
  if (typeof value.externalId !== "string" || !value.externalId) fail("PlatformUnifiedOrder", "missing externalId");
  if (!isOrderState(value.state)) fail("PlatformUnifiedOrder", `unknown state ${String(value.state)}`);
  if (typeof value.placedAt !== "string") fail("PlatformUnifiedOrder", "missing placedAt");
  if (!Array.isArray(value.items)) fail("PlatformUnifiedOrder", "items must be an array");
  if (typeof value.subtotalCents !== "number") fail("PlatformUnifiedOrder", "missing subtotalCents");
  if (value.totalCents !== null && typeof value.totalCents !== "number") {
    fail("PlatformUnifiedOrder", "totalCents must be a number or null");
  }
  if (value.checkoutOrderId !== null && typeof value.checkoutOrderId !== "string") {
    fail("PlatformUnifiedOrder", "checkoutOrderId must be a string or null");
  }
  // Pedido de origem `loja` SEM o id do pedido do checkout seria um pedido
  // unificado sem o registro que ele projeta: a lista perderia o vínculo com a
  // origem, que é justamente o que esta task tem de preservar.
  if (value.origin === "loja" && !value.checkoutOrderId) {
    fail("PlatformUnifiedOrder", "origin loja requires checkoutOrderId");
  }
  if (!Array.isArray(value.events)) fail("PlatformUnifiedOrder", "events must be an array");
  for (const event of value.events) {
    if (!isRecord(event) || typeof event.id !== "string" || !event.id) fail("PlatformOrderEvent", "missing id");
    if (!isOrderState(event.toState)) fail("PlatformOrderEvent", `unknown toState ${String(event.toState)}`);
    if (event.fromState !== null && !isOrderState(event.fromState)) {
      fail("PlatformOrderEvent", `unknown fromState ${String(event.fromState)}`);
    }
    // Transição sem autor não sustenta auditoria: a linha do tempo tem de
    // dizer quem registrou (operador) ou que a origem foi o sistema.
    if (typeof event.actor !== "string" || !event.actor) fail("PlatformOrderEvent", "missing actor");
    if (event.actorKind !== "staff" && event.actorKind !== "system") {
      fail("PlatformOrderEvent", `unknown actorKind ${String(event.actorKind)}`);
    }
    if (typeof event.occurredAt !== "string") fail("PlatformOrderEvent", "missing occurredAt");
  }
  return value as unknown as PlatformUnifiedOrder;
}

export function assertPlatformConversation(value: unknown): PlatformConversation {
  if (!isRecord(value)) fail("PlatformConversation", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformConversation", "missing id");
  if (typeof value.customerId !== "string" || !value.customerId) fail("PlatformConversation", "missing customerId");
  // O banco aceita também `assist` e `paused` (CHECK de channel_conversations.mode); recusar esses
  // valores fazia a conversa inteira abrir como erro assim que o assistente de IA os usava.
  if (value.mode !== "ai" && value.mode !== "human" && value.mode !== "assist" && value.mode !== "paused") fail("PlatformConversation", `unknown mode ${String(value.mode)}`);
  if (value.channel !== "web" && value.channel !== "whatsapp") fail("PlatformConversation", `unknown channel ${String(value.channel)}`);
  if (!Array.isArray(value.messages)) fail("PlatformConversation", "messages must be an array");
  if (typeof value.leadScore !== "number" || value.leadScore < 0 || value.leadScore > 100) {
    fail("PlatformConversation", "leadScore out of range 0-100");
  }
  if (typeof value.updatedAt !== "string") fail("PlatformConversation", "missing updatedAt");
  return value as unknown as PlatformConversation;
}

export function assertPlatformCatalogEntry(value: unknown): PlatformCatalogEntry {
  if (!isRecord(value)) fail("PlatformCatalogEntry", "payload is not an object");
  if (!isRecord(value.product) || typeof value.product.id !== "string") fail("PlatformCatalogEntry", "missing product.id");
  if (value.offer !== null && !isRecord(value.offer)) fail("PlatformCatalogEntry", "offer must be an object or null");
  if (value.offer && typeof (value.offer as Record<string, unknown>).stock !== "number") {
    fail("PlatformCatalogEntry", "offer.stock must be a number");
  }
  return value as unknown as PlatformCatalogEntry;
}

export function assertPricingSummaryResponse(value: unknown): PricingSummaryResponse {
  if (!isRecord(value)) fail("PricingSummaryResponse", "payload is not an object");
  const summary = value.summary;
  if (!isRecord(summary)) fail("PricingSummaryResponse", "missing summary");
  if (typeof summary.minMarginPct !== "number") fail("PricingSummaryResponse", "missing summary.minMarginPct");
  if (summary.avgMarginPct !== null && typeof summary.avgMarginPct !== "number") {
    fail("PricingSummaryResponse", "summary.avgMarginPct must be a number or null");
  }
  if (!Array.isArray(value.rows)) fail("PricingSummaryResponse", "rows must be an array");
  for (const row of value.rows) {
    if (!isRecord(row) || typeof row.productId !== "string") fail("PricingSummaryResponse", "row missing productId");
    if (row.marginPct !== null && typeof row.marginPct !== "number") {
      fail("PricingSummaryResponse", "row.marginPct must be a number or null");
    }
  }
  return value as unknown as PricingSummaryResponse;
}

export function assertPlatformNotification(value: unknown): PlatformNotification {
  if (!isRecord(value)) fail("PlatformNotification", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformNotification", "missing id");
  if (typeof value.type !== "string" || !value.type) fail("PlatformNotification", "missing type");
  if (value.severity !== "info" && value.severity !== "warning" && value.severity !== "critical") {
    fail("PlatformNotification", `unknown severity ${String(value.severity)}`);
  }
  if (typeof value.created_at !== "string") fail("PlatformNotification", "missing created_at");
  return value as unknown as PlatformNotification;
}

export function assertPlatformAuditEvent(value: unknown): PlatformAuditEvent {
  if (!isRecord(value)) fail("PlatformAuditEvent", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformAuditEvent", "missing id");
  if (typeof value.occurredAt !== "string") fail("PlatformAuditEvent", "missing occurredAt");
  if (typeof value.eventType !== "string" || !value.eventType) fail("PlatformAuditEvent", "missing eventType");
  if (typeof value.aggregateId !== "string" || !value.aggregateId) fail("PlatformAuditEvent", "missing aggregateId");
  const actorType = value.actorType;
  if (actorType !== "customer" && actorType !== "human" && actorType !== "ai" && actorType !== "system") {
    fail("PlatformAuditEvent", `unknown actorType ${String(actorType)}`);
  }
  return value as unknown as PlatformAuditEvent;
}

export function assertPlatformCatalogProduct(value: unknown): PlatformCatalogProduct {
  if (!isRecord(value)) fail("PlatformCatalogProduct", "payload is not an object");
  if (typeof value.productId !== "string" || !value.productId) fail("PlatformCatalogProduct", "missing productId");
  if (typeof value.sku !== "string" || !value.sku) fail("PlatformCatalogProduct", "missing sku");
  const operational = value.operational;
  if (!isRecord(operational)) fail("PlatformCatalogProduct", "missing operational layer");
  if (typeof operational.sku !== "string") fail("PlatformCatalogProduct", "missing operational.sku");
  if (!Array.isArray(operational.variants)) fail("PlatformCatalogProduct", "operational.variants must be an array");
  if (!Array.isArray(operational.kitItems)) fail("PlatformCatalogProduct", "operational.kitItems must be an array");
  const commercial = value.commercial;
  if (!isRecord(commercial)) fail("PlatformCatalogProduct", "missing commercial layer");
  if (typeof commercial.published !== "boolean") fail("PlatformCatalogProduct", "missing commercial.published");
  if (!Array.isArray(commercial.media)) fail("PlatformCatalogProduct", "commercial.media must be an array");
  if (value.sellableOffer !== null && !isRecord(value.sellableOffer)) {
    fail("PlatformCatalogProduct", "sellableOffer must be an object or null");
  }
  if (value.sellableOffer && typeof (value.sellableOffer as Record<string, unknown>).stock !== "number") {
    fail("PlatformCatalogProduct", "sellableOffer.stock must be a number");
  }
  if (!Array.isArray(value.b2bPriceTables)) fail("PlatformCatalogProduct", "b2bPriceTables must be an array");
  if (!Array.isArray(value.reservations)) fail("PlatformCatalogProduct", "reservations must be an array");
  for (const reservation of value.reservations) {
    if (!isRecord(reservation) || typeof reservation.quantity !== "number") {
      fail("PlatformCatalogProduct", "reservation.quantity must be a number");
    }
    const status = reservation.status;
    if (status !== "active" && status !== "released" && status !== "consumed") {
      fail("PlatformCatalogProduct", `unknown reservation status ${String(status)}`);
    }
  }
  if (!Array.isArray(value.changeHistory)) fail("PlatformCatalogProduct", "changeHistory must be an array");
  for (const entry of value.changeHistory) {
    if (!isRecord(entry) || typeof entry.fieldKey !== "string") fail("PlatformCatalogProduct", "changeHistory.fieldKey missing");
    const source = entry.source;
    if (source !== "bling_sync" && source !== "platform_edit" && source !== "system") {
      fail("PlatformCatalogProduct", `unknown changeHistory source ${String(source)}`);
    }
  }
  return value as unknown as PlatformCatalogProduct;
}

export function assertBlingCatalogSnapshot(value: unknown): BlingCatalogSnapshot {
  if (!isRecord(value)) fail("BlingCatalogSnapshot", "payload is not an object");
  if (value.source !== "bling" && value.source !== "simulator") {
    fail("BlingCatalogSnapshot", `unknown source ${String(value.source)}`);
  }
  if (value.credential !== "configured" && value.credential !== "missing") {
    fail("BlingCatalogSnapshot", `unknown credential state ${String(value.credential)}`);
  }
  if (typeof value.fetchedAt !== "string") fail("BlingCatalogSnapshot", "missing fetchedAt");
  if (!Array.isArray(value.items)) fail("BlingCatalogSnapshot", "items must be an array");
  for (const item of value.items) {
    if (!isRecord(item)) fail("BlingCatalogSnapshot", "item is not an object");
    if (typeof item.blingId !== "string" || !item.blingId) fail("BlingCatalogSnapshot", "item missing blingId");
    if (typeof item.sku !== "string" || !item.sku) fail("BlingCatalogSnapshot", "item missing sku");
    if (item.productId !== null && typeof item.productId !== "string") {
      fail("BlingCatalogSnapshot", "item.productId must be a string or null");
    }
    if (item.stock !== undefined && typeof item.stock !== "number") fail("BlingCatalogSnapshot", "item.stock must be a number");
    if (item.priceCents !== undefined && typeof item.priceCents !== "number") {
      fail("BlingCatalogSnapshot", "item.priceCents must be a number");
    }
  }
  return value as unknown as BlingCatalogSnapshot;
}

export function assertBlingSyncLogEntry(value: unknown): BlingSyncLogEntry {
  if (!isRecord(value)) fail("BlingSyncLogEntry", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("BlingSyncLogEntry", "missing id");
  if (typeof value.local_id !== "string" || !value.local_id) fail("BlingSyncLogEntry", "missing local_id");
  if (typeof value.created_at !== "string") fail("BlingSyncLogEntry", "missing created_at");
  const entityType = value.entity_type;
  if (entityType !== "product" && entityType !== "order" && entityType !== "invoice" && entityType !== "shipment") {
    fail("BlingSyncLogEntry", `unknown entity_type ${String(entityType)}`);
  }
  if (value.direction !== "push" && value.direction !== "pull") {
    fail("BlingSyncLogEntry", `unknown direction ${String(value.direction)}`);
  }
  if (value.status !== "synced" && value.status !== "conflict" && value.status !== "failed") {
    fail("BlingSyncLogEntry", `unknown status ${String(value.status)}`);
  }
  if (value.bling_id !== null && typeof value.bling_id !== "string") {
    fail("BlingSyncLogEntry", "bling_id must be a string or null");
  }
  return value as unknown as BlingSyncLogEntry;
}

export function assertBlingReprocessResult(value: unknown): BlingReprocessResult {
  if (!isRecord(value)) fail("BlingReprocessResult", "payload is not an object");
  if (value.status !== "queued") fail("BlingReprocessResult", `unexpected status ${String(value.status)}`);
  if (typeof value.entityType !== "string" || !value.entityType) fail("BlingReprocessResult", "missing entityType");
  if (typeof value.entityId !== "string" || !value.entityId) fail("BlingReprocessResult", "missing entityId");
  if (value.queueId !== undefined && typeof value.queueId !== "string") fail("BlingReprocessResult", "queueId must be a string");
  return value as unknown as BlingReprocessResult;
}

export function assertPlatformRmaCase(value: unknown): PlatformRmaCase {
  if (!isRecord(value)) fail("PlatformRmaCase", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformRmaCase", "missing id");
  if (typeof value.customerId !== "string" || !value.customerId) fail("PlatformRmaCase", "missing customerId");
  if (!isRmaState(value.state)) fail("PlatformRmaCase", `unknown state ${String(value.state)}`);
  if (value.resolution !== null && value.resolution !== undefined && !isRmaResolution(value.resolution)) {
    fail("PlatformRmaCase", `unknown resolution ${String(value.resolution)}`);
  }
  if (typeof value.openedAt !== "string") fail("PlatformRmaCase", "missing openedAt");
  if (!Array.isArray(value.events)) fail("PlatformRmaCase", "events must be an array");
  for (const event of value.events) {
    if (!isRecord(event) || typeof event.id !== "string" || !event.id) fail("PlatformRmaEvent", "missing id");
    if (!isRmaState(event.toState)) fail("PlatformRmaEvent", `unknown toState ${String(event.toState)}`);
    if (event.fromState !== null && !isRmaState(event.fromState)) {
      fail("PlatformRmaEvent", `unknown fromState ${String(event.fromState)}`);
    }
    // Passo sem autor não sustenta auditoria: a trilha tem de dizer quem o
    // registrou (operador) ou que a origem foi o sistema.
    if (typeof event.actor !== "string" || !event.actor) fail("PlatformRmaEvent", "missing actor");
    if (event.actorKind !== "staff" && event.actorKind !== "system") {
      fail("PlatformRmaEvent", `unknown actorKind ${String(event.actorKind)}`);
    }
    if (typeof event.occurredAt !== "string") fail("PlatformRmaEvent", "missing occurredAt");
  }
  return value as unknown as PlatformRmaCase;
}

// ---------------------------------------------------------------------------
// Task 14 (brief 3.7) — LOGÍSTICA E EXPEDIÇÃO (`fulfillment_tasks` +
// `fulfillment_events`, migration 0033).
//
// A tarefa de expedição **referencia** o pedido unificado (`platformOrderId`)
// em vez de recadastrá-lo: é a ordem de trabalho do chão de expedição (etapa,
// responsável, marcos de tempo, ocorrências), não uma segunda cópia da venda.
// O vocabulário de etapa/ocorrência vem da máquina pura (espelho dos `check`
// da 0033), não de uma lista declarada aqui.
// ---------------------------------------------------------------------------

/**
 * Passo registrado na ordem de expedição (`fulfillment_events`). É o histórico
 * auditável: quem, quando, de que etapa para qual, por quê, com que dado.
 *
 * `eventKind` distingue o avanço de etapa (`stage_change`) do registro de
 * ocorrência (`occurrence`) e da sua baixa (`occurrence_cleared`). `actorKind:
 * "system"` marca o passo espelhado de um registro de origem (ex.: entrega
 * confirmada pelo rastreio do transportador), nunca apresentado como ação de
 * operador.
 */
export interface FulfillmentEvent {
  id: string;
  fulfillmentTaskId: string;
  eventKind: "stage_change" | "occurrence" | "occurrence_cleared";
  /** `null` no evento de criação (a tarefa não vinha de etapa alguma). */
  fromStage: FulfillmentStage | null;
  /** `null` nos eventos que não mudam etapa (ocorrência/baixa de ocorrência). */
  toStage: FulfillmentStage | null;
  occurrenceKind: OccurrenceKind | null;
  actor: string;
  actorKind: "staff" | "system";
  reason: string;
  payload: Record<string, unknown>;
  occurredAt: string;
}

/**
 * Tarefa de expedição de um pedido (`fulfillment_tasks`). Carrega o estado
 * INTERNO; o estado do transportador vem do rastreio e é mesclado na leitura
 * (`FulfillmentTask.tracking`).
 */
export interface FulfillmentTask {
  id: string;
  platformOrderId: string;
  /** Identificador do pedido no canal de origem, para a fila ser reconhecível. */
  orderExternalId: string;
  orderOrigin: PlatformOrderOrigin;
  customerId: string | null;
  customerName: string | null;
  stage: FulfillmentStage;
  assignedTo: string | null;
  /** Ocorrência pendente que trava a fila; `null` = sem ocorrência pendente. */
  occurrenceKind: OccurrenceKind | null;
  occurrenceNote: string;
  carrier: string | null;
  trackingCode: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  events: FulfillmentEvent[];
  /**
   * Estado do transportador consultado no servidor (rastreio simulado sem
   * credencial). `null` quando ainda não há evento do transportador. É a parte
   * EXTERNA da visão unificada (D-14.4).
   */
  tracking: {
    status: "confirmed" | "shipped" | "in_transit" | "delivered" | "exception" | null;
    note: string;
    source: "carrier" | "simulator";
  };
}

export function assertFulfillmentTask(value: unknown): FulfillmentTask {
  if (!isRecord(value)) fail("FulfillmentTask", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("FulfillmentTask", "missing id");
  if (typeof value.platformOrderId !== "string" || !value.platformOrderId) {
    fail("FulfillmentTask", "missing platformOrderId");
  }
  if (!isFulfillmentStage(value.stage)) fail("FulfillmentTask", `unknown stage ${String(value.stage)}`);
  if (!isOrderOrigin(value.orderOrigin)) fail("FulfillmentTask", `unknown orderOrigin ${String(value.orderOrigin)}`);
  if (value.occurrenceKind !== null && !isOccurrenceKind(value.occurrenceKind)) {
    fail("FulfillmentTask", `unknown occurrenceKind ${String(value.occurrenceKind)}`);
  }
  if (typeof value.createdAt !== "string") fail("FulfillmentTask", "missing createdAt");
  if (!Array.isArray(value.events)) fail("FulfillmentTask", "events must be an array");
  for (const event of value.events) {
    if (!isRecord(event) || typeof event.id !== "string" || !event.id) fail("FulfillmentEvent", "missing id");
    const eventKind = event.eventKind;
    if (eventKind !== "stage_change" && eventKind !== "occurrence" && eventKind !== "occurrence_cleared") {
      fail("FulfillmentEvent", `unknown eventKind ${String(eventKind)}`);
    }
    if (event.fromStage !== null && !isFulfillmentStage(event.fromStage)) {
      fail("FulfillmentEvent", `unknown fromStage ${String(event.fromStage)}`);
    }
    if (event.toStage !== null && !isFulfillmentStage(event.toStage)) {
      fail("FulfillmentEvent", `unknown toStage ${String(event.toStage)}`);
    }
    // Passo sem autor não sustenta auditoria: a trilha tem de dizer quem o
    // registrou (operador) ou que a origem foi o sistema (rastreio).
    if (typeof event.actor !== "string" || !event.actor) fail("FulfillmentEvent", "missing actor");
    if (event.actorKind !== "staff" && event.actorKind !== "system") {
      fail("FulfillmentEvent", `unknown actorKind ${String(event.actorKind)}`);
    }
    if (typeof event.occurredAt !== "string") fail("FulfillmentEvent", "missing occurredAt");
  }
  const tracking = value.tracking;
  if (!isRecord(tracking)) fail("FulfillmentTask", "missing tracking");
  const trackingStatus = tracking.status;
  if (
    trackingStatus !== null &&
    trackingStatus !== "confirmed" &&
    trackingStatus !== "shipped" &&
    trackingStatus !== "in_transit" &&
    trackingStatus !== "delivered" &&
    trackingStatus !== "exception"
  ) {
    fail("FulfillmentTask", `unknown tracking.status ${String(trackingStatus)}`);
  }
  if (tracking.source !== "carrier" && tracking.source !== "simulator") {
    fail("FulfillmentTask", `unknown tracking.source ${String(tracking.source)}`);
  }
  return value as unknown as FulfillmentTask;
}

// ---------------------------------------------------------------------------
// Task 16 (brief 3.8) — Financeiro gerencial e inteligência comercial
// (`financial_reconciliations`/`managerial_accounts`/`managerial_entries`/
// `order_margins`/`product_margins`, migration 0035).
//
// Os contratos abaixo são GERENCIAIS, nunca fiscais: a conciliação LÊ o
// pagamento já registrado e aponta divergência, a margem é a REALIZADA
// (receita − taxa − comissão − custo) reusando o piso de `packages/pricing`, e
// nenhum deles emite nota ou move dinheiro. `source` distingue número de
// credencial real do número do simulador — crédito ausente nunca vira dado
// falso.
// ---------------------------------------------------------------------------

export interface FinancialReconciliation {
  id: string;
  platformOrderId: string;
  orderExternalId: string | null;
  orderOrigin: PlatformOrderOrigin | null;
  paymentReference: string | null;
  grossCents: number;
  paymentFeeCents: number;
  commissionCents: number;
  costCents: number;
  expectedNetCents: number;
  /** Líquido efetivamente repassado; `null` enquanto o repasse não chegou. */
  actualNetCents: number | null;
  status: ReconciliationState;
  divergenceReason: string;
  /** Diferença (efetivo − esperado); `null` enquanto não há repasse. */
  differenceCents: number | null;
  source: "real" | "simulator";
  reconciledAt: string | null;
  createdAt: string;
}

export interface ManagerialAccount {
  id: string;
  key: string;
  name: string;
  kind: ManagerialAccountKind;
  active: boolean;
}

export interface ManagerialEntry {
  id: string;
  accountId: string;
  accountKey: string;
  accountName: string;
  accountKind: ManagerialAccountKind;
  reconciliationId: string | null;
  platformOrderId: string | null;
  orderExternalId: string | null;
  productId: string | null;
  productName: string | null;
  /** Valor SINALIZADO pela convenção da conta (receita +, deduções −). */
  amountCents: number;
  memo: string;
  entryDate: string;
}

export interface OrderMargin {
  platformOrderId: string;
  orderExternalId: string | null;
  orderOrigin: PlatformOrderOrigin | null;
  revenueCents: number;
  paymentFeeCents: number;
  commissionCents: number;
  costCents: number;
  marginCents: number;
  /** Percentual de margem; `null` quando não há receita para dividir. */
  marginPct: number | null;
  belowFloor: boolean;
  /** Margem abaixo do piso => correção por reprecificação exige aprovação humana. */
  needsHumanApproval: boolean;
  computedAt: string;
}

export interface ProductMargin {
  productId: string;
  productName: string | null;
  revenueCents: number;
  costCents: number;
  marginCents: number;
  marginPct: number | null;
  belowFloor: boolean;
  needsHumanApproval: boolean;
  orderCount: number;
  computedAt: string;
}

/**
 * Resumo financeiro gerencial — o payload da rota de rentabilidade. Carrega o
 * piso de margem aplicado e o `source` agregado para a tela declarar quando os
 * números vêm do simulador (credencial ausente).
 */
export interface FinancialSummaryResponse {
  summary: {
    reconciled: number;
    diverged: number;
    pending: number;
    revenueCents: number;
    paymentFeeCents: number;
    commissionCents: number;
    costCents: number;
    marginCents: number;
    marginPct: number | null;
    belowFloorOrders: number;
    minMarginPct: number;
    source: "real" | "simulator";
  };
  reconciliations: FinancialReconciliation[];
  orderMargins: OrderMargin[];
  productMargins: ProductMargin[];
  entries: ManagerialEntry[];
  accounts: ManagerialAccount[];
}

export function assertFinancialReconciliation(value: unknown): FinancialReconciliation {
  if (!isRecord(value)) fail("FinancialReconciliation", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("FinancialReconciliation", "missing id");
  if (typeof value.platformOrderId !== "string" || !value.platformOrderId) {
    fail("FinancialReconciliation", "missing platformOrderId");
  }
  if (!isReconciliationState(value.status)) fail("FinancialReconciliation", `unknown status ${String(value.status)}`);
  if (typeof value.grossCents !== "number") fail("FinancialReconciliation", "missing grossCents");
  if (typeof value.expectedNetCents !== "number") fail("FinancialReconciliation", "missing expectedNetCents");
  if (value.actualNetCents !== null && typeof value.actualNetCents !== "number") {
    fail("FinancialReconciliation", "actualNetCents must be a number or null");
  }
  if (value.source !== "real" && value.source !== "simulator") {
    fail("FinancialReconciliation", `unknown source ${String(value.source)}`);
  }
  if (typeof value.createdAt !== "string") fail("FinancialReconciliation", "missing createdAt");
  return value as unknown as FinancialReconciliation;
}

export function assertManagerialAccount(value: unknown): ManagerialAccount {
  if (!isRecord(value)) fail("ManagerialAccount", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("ManagerialAccount", "missing id");
  if (typeof value.key !== "string" || !value.key) fail("ManagerialAccount", "missing key");
  if (typeof value.name !== "string" || !value.name) fail("ManagerialAccount", "missing name");
  if (!isManagerialAccountKind(value.kind)) fail("ManagerialAccount", `unknown kind ${String(value.kind)}`);
  if (typeof value.active !== "boolean") fail("ManagerialAccount", "missing active");
  return value as unknown as ManagerialAccount;
}

export function assertManagerialEntry(value: unknown): ManagerialEntry {
  if (!isRecord(value)) fail("ManagerialEntry", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("ManagerialEntry", "missing id");
  if (typeof value.accountKey !== "string" || !value.accountKey) fail("ManagerialEntry", "missing accountKey");
  if (!isManagerialAccountKind(value.accountKind)) {
    fail("ManagerialEntry", `unknown accountKind ${String(value.accountKind)}`);
  }
  if (typeof value.amountCents !== "number") fail("ManagerialEntry", "missing amountCents");
  if (typeof value.entryDate !== "string" || !value.entryDate) fail("ManagerialEntry", "missing entryDate");
  return value as unknown as ManagerialEntry;
}

export function assertOrderMargin(value: unknown): OrderMargin {
  if (!isRecord(value)) fail("OrderMargin", "payload is not an object");
  if (typeof value.platformOrderId !== "string" || !value.platformOrderId) {
    fail("OrderMargin", "missing platformOrderId");
  }
  if (typeof value.revenueCents !== "number") fail("OrderMargin", "missing revenueCents");
  if (typeof value.marginCents !== "number") fail("OrderMargin", "missing marginCents");
  if (value.marginPct !== null && typeof value.marginPct !== "number") {
    fail("OrderMargin", "marginPct must be a number or null");
  }
  if (typeof value.belowFloor !== "boolean") fail("OrderMargin", "missing belowFloor");
  if (typeof value.needsHumanApproval !== "boolean") fail("OrderMargin", "missing needsHumanApproval");
  return value as unknown as OrderMargin;
}

export function assertProductMargin(value: unknown): ProductMargin {
  if (!isRecord(value)) fail("ProductMargin", "payload is not an object");
  if (typeof value.productId !== "string" || !value.productId) fail("ProductMargin", "missing productId");
  if (typeof value.revenueCents !== "number") fail("ProductMargin", "missing revenueCents");
  if (typeof value.marginCents !== "number") fail("ProductMargin", "missing marginCents");
  if (value.marginPct !== null && typeof value.marginPct !== "number") {
    fail("ProductMargin", "marginPct must be a number or null");
  }
  if (typeof value.belowFloor !== "boolean") fail("ProductMargin", "missing belowFloor");
  if (typeof value.needsHumanApproval !== "boolean") fail("ProductMargin", "missing needsHumanApproval");
  return value as unknown as ProductMargin;
}

export function assertFinancialSummaryResponse(value: unknown): FinancialSummaryResponse {
  if (!isRecord(value)) fail("FinancialSummaryResponse", "payload is not an object");
  if (!isRecord(value.summary)) fail("FinancialSummaryResponse", "missing summary");
  const summary = value.summary;
  if (typeof summary.minMarginPct !== "number") fail("FinancialSummaryResponse", "summary missing minMarginPct");
  if (summary.source !== "real" && summary.source !== "simulator") {
    fail("FinancialSummaryResponse", `unknown summary.source ${String(summary.source)}`);
  }
  if (!Array.isArray(value.reconciliations)) fail("FinancialSummaryResponse", "reconciliations must be an array");
  if (!Array.isArray(value.orderMargins)) fail("FinancialSummaryResponse", "orderMargins must be an array");
  if (!Array.isArray(value.productMargins)) fail("FinancialSummaryResponse", "productMargins must be an array");
  if (!Array.isArray(value.entries)) fail("FinancialSummaryResponse", "entries must be an array");
  if (!Array.isArray(value.accounts)) fail("FinancialSummaryResponse", "accounts must be an array");
  value.reconciliations.forEach(assertFinancialReconciliation);
  value.orderMargins.forEach(assertOrderMargin);
  value.productMargins.forEach(assertProductMargin);
  value.entries.forEach(assertManagerialEntry);
  value.accounts.forEach(assertManagerialAccount);
  return value as unknown as FinancialSummaryResponse;
}

// ---------------------------------------------------------------------------
// Entrega 0 (R3–R5) — configuração de integrações. O tipo vem de
// packages/config; aqui só validamos a BORDA (o JSON chegou do servidor). A
// validação confirma que a resposta traz metadados — estado, flag e os nomes
// de secret com um booleano configurado — e NUNCA um valor de segredo (o
// contrato do pacote não tem campo de valor, e o teste varre o JSON para
// garantir que nenhum valor vaze).
// ---------------------------------------------------------------------------

/** Reexport do contrato de integração (D-R3R5-02): o MESMO que o backend serve. */
export type PlatformIntegrationStatus = SerializedIntegration;

export function assertPlatformIntegrationStatus(value: unknown): PlatformIntegrationStatus {
  if (!isRecord(value)) fail("PlatformIntegrationStatus", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PlatformIntegrationStatus", "missing id");
  if (typeof value.name !== "string" || !value.name) fail("PlatformIntegrationStatus", "missing name");
  if (value.state !== "real" && value.state !== "simulator") {
    fail("PlatformIntegrationStatus", `unknown state ${String(value.state)}`);
  }
  if (!isRecord(value.flag) || typeof value.flag.name !== "string" || typeof value.flag.enabled !== "boolean") {
    fail("PlatformIntegrationStatus", "flag must carry a name and an enabled boolean");
  }
  if (!Array.isArray(value.secrets)) fail("PlatformIntegrationStatus", "secrets must be an array");
  value.secrets.forEach((secret) => {
    // Só o NOME e o booleano `configured` são aceitos — um campo de valor aqui
    // seria o próprio vazamento que o invariante proíbe.
    if (!isRecord(secret) || typeof secret.name !== "string" || typeof secret.configured !== "boolean") {
      fail("PlatformIntegrationStatus", "secret must carry a name and a configured boolean");
    }
  });
  if (!Array.isArray(value.missing)) fail("PlatformIntegrationStatus", "missing must be an array");
  return value as unknown as PlatformIntegrationStatus;
}

/**
 * Plano Loja/Twotrees, Fase 7/8 — marketing admin→loja. Feature flag genérica
 * (liga/desliga) + destaque da faixa de pré-venda. Mesmo contrato do
 * storefront-marketing-runtime.
 */
export type MarketingFeatureKey = "presale_ribbon" | "spin_wheel" | "video_spotlights" | "banners";

export interface MarketingFeatureFlag {
  featureKey: MarketingFeatureKey;
  enabled: boolean;
}

export interface PresaleHighlight {
  id: string;
  productId: string;
  headline: string;
  active: boolean;
  position: number;
  startsAt: string | null;
  endsAt: string | null;
}

const MARKETING_FEATURE_KEYS: readonly MarketingFeatureKey[] = ["presale_ribbon", "spin_wheel", "video_spotlights", "banners"];

export function assertMarketingFeatureFlag(value: unknown): MarketingFeatureFlag {
  if (!isRecord(value)) fail("MarketingFeatureFlag", "payload is not an object");
  if (!MARKETING_FEATURE_KEYS.includes(value.featureKey as MarketingFeatureKey)) {
    fail("MarketingFeatureFlag", `unknown featureKey ${String(value.featureKey)}`);
  }
  if (typeof value.enabled !== "boolean") fail("MarketingFeatureFlag", "missing enabled");
  return value as unknown as MarketingFeatureFlag;
}

export function assertPresaleHighlight(value: unknown): PresaleHighlight {
  if (!isRecord(value)) fail("PresaleHighlight", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("PresaleHighlight", "missing id");
  if (typeof value.productId !== "string" || !value.productId) fail("PresaleHighlight", "missing productId");
  if (typeof value.headline !== "string" || !value.headline) fail("PresaleHighlight", "missing headline");
  if (typeof value.active !== "boolean") fail("PresaleHighlight", "missing active");
  if (typeof value.position !== "number") fail("PresaleHighlight", "missing position");
  if (value.startsAt !== null && typeof value.startsAt !== "string") fail("PresaleHighlight", "startsAt must be string or null");
  if (value.endsAt !== null && typeof value.endsAt !== "string") fail("PresaleHighlight", "endsAt must be string or null");
  return value as unknown as PresaleHighlight;
}

/**
 * Plano Loja/Twotrees, Fase 9 — banner promocional da home. Imagem por URL
 * (sem storage no projeto, mesmo padrão de `product_media.url`), link
 * opcional (banner pode ser só institucional) e `altText` obrigatório
 * (acessibilidade — é a única descrição textual da imagem).
 */
export interface Banner {
  id: string;
  imageUrl: string;
  altText: string;
  linkUrl: string | null;
  active: boolean;
  position: number;
  startsAt: string | null;
  endsAt: string | null;
}

export function assertBanner(value: unknown): Banner {
  if (!isRecord(value)) fail("Banner", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("Banner", "missing id");
  if (typeof value.imageUrl !== "string" || !value.imageUrl) fail("Banner", "missing imageUrl");
  if (typeof value.altText !== "string" || !value.altText) fail("Banner", "missing altText");
  if (value.linkUrl !== null && typeof value.linkUrl !== "string") fail("Banner", "linkUrl must be string or null");
  if (typeof value.active !== "boolean") fail("Banner", "missing active");
  if (typeof value.position !== "number") fail("Banner", "missing position");
  if (value.startsAt !== null && typeof value.startsAt !== "string") fail("Banner", "startsAt must be string or null");
  if (value.endsAt !== null && typeof value.endsAt !== "string") fail("Banner", "endsAt must be string or null");
  return value as unknown as Banner;
}

/**
 * Plano Loja/Twotrees, Fase 10 — cupom de desconto. `usedCount` é derivado
 * (contagem de resgates) e nunca editável pela tela. Sem DELETE no contrato:
 * cupom usado tem pedidos que dependem dele para a proveniência do desconto
 * — só desativa (`active=false`, ver checkout-runtime/migration 0047).
 */
export type CouponDiscountType = "percentage" | "fixed";

export interface Coupon {
  id: string;
  code: string;
  discountType: CouponDiscountType;
  discountValue: number;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  minSubtotalCents: number | null;
  maxUses: number | null;
  maxUsesPerCustomer: number | null;
  usedCount: number;
}

export function assertCoupon(value: unknown): Coupon {
  if (!isRecord(value)) fail("Coupon", "payload is not an object");
  if (typeof value.id !== "string" || !value.id) fail("Coupon", "missing id");
  if (typeof value.code !== "string" || !value.code) fail("Coupon", "missing code");
  if (value.discountType !== "percentage" && value.discountType !== "fixed") fail("Coupon", "discountType must be percentage or fixed");
  if (typeof value.discountValue !== "number") fail("Coupon", "missing discountValue");
  if (typeof value.active !== "boolean") fail("Coupon", "missing active");
  if (value.startsAt !== null && typeof value.startsAt !== "string") fail("Coupon", "startsAt must be string or null");
  if (value.endsAt !== null && typeof value.endsAt !== "string") fail("Coupon", "endsAt must be string or null");
  if (value.minSubtotalCents !== null && typeof value.minSubtotalCents !== "number") fail("Coupon", "minSubtotalCents must be number or null");
  if (value.maxUses !== null && typeof value.maxUses !== "number") fail("Coupon", "maxUses must be number or null");
  if (value.maxUsesPerCustomer !== null && typeof value.maxUsesPerCustomer !== "number") fail("Coupon", "maxUsesPerCustomer must be number or null");
  if (typeof value.usedCount !== "number") fail("Coupon", "missing usedCount");
  return value as unknown as Coupon;
}
